import {
  Autocomplete,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  PopperProps,
  Popper,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommunityUserRole } from '../../../modules/communities/models/communities.types';
import {
  CommunityNav,
  ContactCommunity,
} from '../../../modules/contacts/models/contacts.types';
import {
  UserState,
  useUserStore,
} from '../../../modules/user/stores/use-user.store';
import { IconButton } from '../../../shared/ui/IconButton/IconButton';
import { TextButton } from '../../../shared/ui/TextButton/TextButton';
import { CommunityLogoName } from '../../navigation/NavCommunity/NavCommunity';
import { NavCommunityButton } from '../../navigation/NavCommunityButton/NavCommunityButton';
import { NaviTitle } from '../../navigation/NaviTitle/NaviTitle';
import create_group_bg from '../../../assets/pictures/dashboard/create_group_background.png';
import { InputFlat } from '../../../shared/ui/InputFlat/InputFlat';
import { Option, ResultState } from '../../../shared/models/shared.types';
import { useContacts } from '../../../modules/contacts/hooks/use-contacts.hook';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faChevronDown,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import { useMutation } from 'react-query';
import { useContactsHttp } from '../../../modules/contacts/hooks/use-contacts-http.hook';
import { GroupRoomSettings } from '../../../modules/newscenter/models/news-center.types';
import {
  SharedState,
  useSharedStore,
} from '../../../shared/stores/use-shared.store';

const communityMock: ContactCommunity = {
  member_role: CommunityUserRole.Admin,
  groups: [
    {
      id: '8486b44a-abb3-40a2-b7f5-3a8e2a05dbf6',
      name: 'P4 Dev',
      description: '',
      chat_room: {
        id: '8486b44a-abb3-40a2-b7f5-3a8e2a05dbf6',
      },
    },
    {
      id: '69ab86b2-6f92-4194-9f2a-5b41ee41eb97',
      name: 'P4 Entwicklung',
      description: '',
      chat_room: {
        id: '69ab86b2-6f92-4194-9f2a-5b41ee41eb97',
      },
    },
  ],
  folders: [
    {
      id: '43d2cd13-a89d-4d6d-8cc9-9a529e27adb7',
      name: 'test',
      groups: [
        {
          id: '1345a2d9-c172-4195-a260-3a84368d3a21',
          name: 'Test',
          description: '',
        },
        {
          id: 'e8fb5319-2b40-4ae4-b4e2-afca4390fd2c',
          name: 'Test',
          description: '',
        },
      ],
    },
  ],
  id: '8e6ea06b-0b0a-40f9-ba0f-a64746d1d51a',
  name: 'M.F.G. Pengueen UG',
  employee_name: '',
  member_name: '',
  avatar: {
    id: '4a40e586-9bbf-4ee7-a550-0da24e8f22fa',
    url: 'https://files.pengueen.de/api/download/6b0dfc56-6d62-4376-aff4-ae380f5487d5.png',
  },
};

// Custom Popper to apply small font to dropdown list
const SmallFontPopper = (props: PopperProps) => {
  return (
    <Popper
      {...props}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 4],
          },
        },
      ]}
      sx={{
        '& .MuiAutocomplete-paper': {
          fontSize: '0.75rem',
        },
        '& .MuiAutocomplete-listbox': {
          fontSize: '0.75rem',
        },
      }}
    />
  );
};

type SelectContactsProps = {
  community?: CommunityNav;
  toolOption?: boolean;
  open: boolean;
  onClose: () => void;
};

const SelectGroupTeam = ({
  community: selectedCommunity,
  toolOption,
  open,
  onClose,
}: SelectContactsProps) => {
  const { t } = useTranslation();
  const { getContactFoldersEffect } = useContacts();
  const { contactGroupPost } = useContactsHttp();
  const inputRef = useRef<HTMLInputElement | null>(null); // For input focus

  // Component state
  const [profile] = useUserStore((state: UserState) => [state.profile]);
  const [community, setCommunity] = useState<ContactCommunity>(
    selectedCommunity ?? communityMock
  );
  const [groupName, setGroupName] = useState<string>('');
  const [groupDescription, setGroupDescription] = useState<string>('');
  const [isTeamActive, setIsTeamActive] = useState<boolean>(false);
  const [foldersOptions, setFoldersOptions] = useState<Option[]>([]);
  const [selectedFolderOption, setSelectedFolderOption] =
    useState<Option | null>(null);
  const [openPopper, setOpenPopper] = useState(false); // Control dropdown open

  // Shared store state
  const [setNotification] = useSharedStore((state: SharedState) => [
    state.setNotification,
  ]);

  // ######### //
  // MUTATIONS //
  // ######### //

  const contactGroupPostMutation = useMutation(
    (settings: GroupRoomSettings) => contactGroupPost(settings),
    {
      // retry: (failureCount, error: any) => handleRetry(failureCount, error),
      onSettled: (data, error) => {
        onClose();

        if (data) {
          // TODO aktivation navigation

          setNotification({
            title: `${t('contacts.toolbar.group.create')}`,
            state: ResultState.Success,
          });
        }
        if (error) {
          console.error('Error posting contact group:');
          setNotification({
            title: `Die Gruppe wurde nicht ordnungsgemäß erstellt.`,
            state: ResultState.Error,
          });
        }
      },
    }
  );

  useEffect(() => {
    setFoldersOptions(getContactFoldersEffect(community.folders ?? []));
  }, [community]);

  // Handle selection or creation of a folder option
  const folderOnChange = useCallback(
    (event, newValue: Option | string | null) => {
      if (typeof newValue === 'string') {
        // User entered a new value
        const newOption = { placeholder: newValue, value: uuidv4() };
        if (!foldersOptions.find((opt) => opt.value === newValue)) {
          setFoldersOptions((prev) => [...prev, newOption]);
        }
        setSelectedFolderOption(newOption);
      } else {
        // User selected from existing options or cleared the input
        setSelectedFolderOption(newValue);
      }
    },
    [foldersOptions]
  );

  const handleIconClick = () => {
    if (inputRef.current) {
      inputRef.current.focus(); // Focus input
    }
    setOpenPopper(true); // Open dropdown
  };

  const handleCreateGroup = () => {
    const settings: GroupRoomSettings = {
      community_id: community.id,
      folder_option: [selectedFolderOption],
      name: groupName,
      description: groupDescription,
      is_team_active: isTeamActive,
    };

    contactGroupPostMutation.mutate(settings);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{
        paper:
          'md:w-3/4 lg:w-2/3 xl:w-3/5 min-w-[600px] min-h-[50vh] overflow-x-hidden',
      }}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      scroll="body"
      sx={{
        '& .MuiPaper-root': {
          backgroundImage: 'none',
          margin: 0,
          padding: 0,
          maxWidth: '600px !important',
        },
      }}
    >
      <IconButton
        classes="absolute right-1 top-1"
        icon={['fas', 'times']}
        onClick={onClose}
      />
      <DialogTitle
        sx={{
          padding: '1.5rem',
          paddingTop: '2rem',
          height: '120px',
        }}
        className="flex font-semibold items-center justify-between"
      >
        <div className="font-semibold text-2xl">
          <Box
            className="font-semibold text-2xl"
            sx={{ color: 'primary.main' }}
          >
            Hallo {profile?.personal_data?.first_name},
          </Box>
          <Box className="text-lg">
            {toolOption
              ? 'lege hier eine Gruppe an und wähle erste Tools aus.'
              : 'lege hier eine Gruppe an.'}
          </Box>
        </div>
      </DialogTitle>

      <Box
        className="flex flex-col gap-2 p-6"
        sx={{ bgcolor: '#D5ECFC', minHeight: '120px' }}
      >
        <NaviTitle title="Meine Unternehmung" />
        <CommunityLogoName community={community} />
        <Box className="ml-4">
          {/* For Admin */}
          {community.member_role === CommunityUserRole.Admin && (
            <NavCommunityButton
              onClick={() => {}}
              title={t('app.admin')}
              icon={['fal', 'users']}
              classes="ml-2"
            />
          )}
          {/* For Employee */}
          {community.member_role !== CommunityUserRole.Member && (
            <NavCommunityButton
              onClick={() => {}}
              title={
                community.employee_name
                  ? community.employee_name
                  : t('app.employee')
              }
              icon={['fal', 'users']}
              classes="ml-2"
            />
          )}
          {/* For Member */}
          {community.member_role === CommunityUserRole.Member ||
            community.member_role === CommunityUserRole.Employee ||
            (community.member_role === CommunityUserRole.Admin && (
              <NavCommunityButton
                onClick={() => {}}
                title={
                  community.member_name
                    ? community.member_name
                    : t('app.member')
                }
                icon={['fal', 'users']}
                classes="ml-2"
              />
            ))}
          <NaviTitle title="Meine Gruppen / Teams" classes="mt-1" />
          <div className="flex flex-row items-center text-xs mb-1">
            <FontAwesomeIcon
              icon={faChevronDown}
              style={{ color: 'text.secondary', cursor: 'pointer' }}
              onClick={() => setOpenPopper(true)}
            />
            <Autocomplete
              open={openPopper}
              onOpen={() => setOpenPopper(true)}
              onClose={() => setOpenPopper(false)}
              className="w-36 mx-2"
              sx={{
                '& .MuiOutlinedInput-root ': {
                  // height: '52px',
                  fontSize: '0.75rem',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  backgroundColor: 'inherit',
                  border: 0,
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                  {
                    borderColor: 'inherit',
                  },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                  {
                    borderColor: 'primary.main',
                  },
              }}
              freeSolo
              getOptionLabel={(option: Option | string) =>
                typeof option === 'string' ? option : option.placeholder
              }
              isOptionEqualToValue={(option, value) =>
                option.value ===
                (typeof value === 'string' ? value : value.value)
              }
              value={selectedFolderOption}
              onChange={folderOnChange}
              options={foldersOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Sammlung"
                  inputRef={inputRef} // Connect to ref for external focus
                  sx={{
                    '& input::placeholder': {
                      fontStyle: 'italic',
                    },
                  }}
                />
              )}
              PopperComponent={SmallFontPopper}
            />
            <FontAwesomeIcon
              icon={faPen}
              style={{ color: 'text.secondary', cursor: 'pointer' }}
              onClick={handleIconClick}
            />
          </div>
          <p className="text-xs text-gray-500 pl-5">
            Du kannst diese Gruppe über das Stiftsymbol einer Sammlung
            hinzufügen. Wähle eine bestehende oder lege eine neue an, indem du
            eine Bezeichnung eingibst und auf Enter drückst.
          </p>
        </Box>
        <div className="mb-2 pl-8">
          <label className="block font-semibold mb-1">
            Wie soll deine Gruppe heißen?
          </label>
          <InputFlat
            type="text"
            placeholder="Bezeichnung"
            preset="White"
            value={groupName}
            onChange={(value) => setGroupName(value)}
          />
        </div>

        <div className="mb-2 pl-8">
          <label className="block font-semibold mb-1">
            Beschreibe kurz den Zweck der Gruppe.
          </label>
          <InputFlat
            placeholder="Kurze Beschreibung"
            preset="White"
            multiline={3}
            value={groupDescription}
            onChange={(value) => setGroupDescription(value)}
          />
        </div>
      </Box>
      <div className="relative">
        <Box className="p-5" sx={{ zIndex: 10 }}>
          <label className="block font-semibold mb-1">
            Erstelle ein aktives Team
          </label>
          <label className="inline-flex items-start text-sm text-gray-700 space-x-2">
            <input
              type="checkbox"
              className="mt-1"
              checked={isTeamActive}
              onChange={() => setIsTeamActive(!isTeamActive)}
            />
            <span>
              Ich möchte, dass die Mitglieder dieser Gruppe Pengueen{' '}
              <strong>für ihre Zusammenarbeit</strong> nutzen. Du brauchst dafür
              eine Team-Lizenz. Hier kommst du zu unserer{' '}
              <Box
                component="span"
                className="underline font-semibold cursor-pointer"
              >
                Preisliste
              </Box>{' '}
              <FontAwesomeIcon
                icon={faArrowUpRightFromSquare}
                className="cursor-pointer"
              />
            </span>
          </label>
        </Box>
        {/* Background Image
        <Box
          component="img"
          className="absolute inset-0 -top-24 pointer-events-none"
          sx={{ zIndex: 1 }}
          src={create_group_bg}
          alt="create_group_background"
        /> */}
      </div>
      <DialogContent className="border-b-0" sx={{ padding: '1.5rem' }}>
        <div className="flex justify-end mt-4">
          <TextButton
            classes="mr-2"
            preset="underline"
            textTransform="none"
            onClick={onClose}
          >
            {t('app.cancel')}
          </TextButton>
          <TextButton
            preset="primary"
            textTransform="none"
            disabled={!groupName.trim()}
            onClick={handleCreateGroup}
          >
            {toolOption
              ? 'Weiter zur Tool-Auswahl'
              : t('templates.sections.create.button')}
          </TextButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SelectGroupTeam;
